/**
 * Environment state + API base resolution for the SEED Expense Management System.
 *
 * One frontend, two backends. Everyone uses Production. Admins and above also
 * get the environment card on the home page, which enters Test: the choice is
 * persisted here and points every API call at the test backend's own Cloud Run
 * URL (REACT_APP_TEST_API_URL). Test data lives in a separate database, so prod
 * data is never touched. Because the two backends are on different hosts, their
 * cookies isolate automatically. The test backend itself refuses anyone whose
 * PRODUCTION role is plain user, so hiding the card is not the only lock.
 *
 * SAFETY: test mode is gated behind TEST_MODE_ENABLED. Until the test backend,
 * the load-balancer route, and ALL frontend services are wired, this stays
 * false so the card cannot send any request (or submission) to a stack that
 * isn't fully ready. Flip it to true only once the whole path is live.
 */

// Renamed at go-live (2026-11-03) so a Test choice saved while the card was
// open to everyone is forgotten and every browser starts on Production.
const ENV_STORAGE_KEY = 'seed_environment_live';

// Master switch - see SAFETY note above.
export const TEST_MODE_ENABLED = true;

// Run the WHOLE site against Test and hide Production. True for the test phase;
// false since go-live (2026-11-03).
export const FORCE_TEST_ENVIRONMENT = false;

// API bases, baked at build time (Create React App). Read at call time so tests
// that set process.env before invoking a service work.
//   REACT_APP_API_URL       → prod backend Cloud Run URL
//   REACT_APP_TEST_API_URL  → test backend Cloud Run URL (own host)
const prodApiBase = () => process.env.REACT_APP_API_URL || '';
// Stable Cloud Run URL of the test backend. Used as the default so no build-arg
// is required; a REACT_APP_TEST_API_URL build override still takes precedence.
const TEST_API_URL_DEFAULT = 'https://expense-backend-test-wh5spyplxq-uc.a.run.app';
const testApiBase = () => process.env.REACT_APP_TEST_API_URL || TEST_API_URL_DEFAULT;

/**
 * The environment the user is currently operating in: 'test' | 'production'.
 * Always 'production' while TEST_MODE_ENABLED is false, and always 'test' while
 * FORCE_TEST_ENVIRONMENT is true.
 */
export const getEnvironment = () => {
  if (!TEST_MODE_ENABLED) {
    return 'production';
  }
  if (FORCE_TEST_ENVIRONMENT) {
    return 'test';
  }
  try {
    return localStorage.getItem(ENV_STORAGE_KEY) === 'test' ? 'test' : 'production';
  } catch {
    return 'production';
  }
};

/** Persist the chosen environment. Callers should reload so module-level bases re-resolve. */
export const setEnvironment = (env) => {
  try {
    if (env === 'test') {
      localStorage.setItem(ENV_STORAGE_KEY, 'test');
    } else {
      localStorage.removeItem(ENV_STORAGE_KEY);
    }
  } catch {
    /* localStorage unavailable - default to production */
  }
};

export const isTestEnvironment = () => getEnvironment() === 'test';

/** Whether the environment card (Production/Test chooser) exists at all. */
export const isTestModeAvailable = () => TEST_MODE_ENABLED && !FORCE_TEST_ENVIRONMENT;

/**
 * Whether to offer this person the environment card. In Production: admins and
 * above only (any role but plain user). In Test: always, signed in or not, so
 * whoever is there - including someone the test backend has just refused - can
 * get back to Production.
 */
export const canChooseEnvironment = (user) =>
  isTestModeAvailable()
  && (isTestEnvironment() || (!!user?.role && user.role !== 'user'));

/**
 * Base URL for all API calls. In test mode it points at the test backend's own
 * Cloud Run URL; otherwise the prod backend URL.
 */
export const getApiBaseUrl = () => (isTestEnvironment() ? testApiBase() : prodApiBase());
